// StrategyCard - unified strategy card with accordion expand/collapse
// Handles both recommended and non-recommended strategies in a single component

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { Button } from '@design-system/components/button/button';
import { Typography } from '@design-system/components/typography/typography';
import {
  StrategyVM,
  CaseStudyVM,
  getStrategyEmoji,
} from '../../view-model/Strategy.viewModel';
import { StrategyCaseStudyCard } from './StrategyCaseStudyCard';
import * as styles from './StrategyCard.css';

export type StrategyCardProps = {
  strategy: StrategyVM;
  caseStudy: CaseStudyVM | null;
  isExpanded: boolean;
  isRecommended: boolean;
  onToggle: () => void;
  onSelect: (strategyId: string) => void;
};

const MAX_FEATURES_DISPLAY = 6;

export const StrategyCard = ({
  strategy,
  caseStudy,
  isExpanded,
  isRecommended,
  onToggle,
  onSelect,
}: StrategyCardProps) => {
  const displayFeatures = strategy.features.slice(0, MAX_FEATURES_DISPLAY);
  const emoji = getStrategyEmoji(strategy.name);
  const hasExpandableContent = displayFeatures.length > 0 || caseStudy !== null;

  const handleSelect = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(strategy.id);
  };

  const cardClassName = [
    styles.card,
    isRecommended ? styles.cardRecommended : '',
  ]
    .filter(Boolean)
    .join(' ');

  const expandableWrapperClassName = [
    styles.expandableWrapper,
    isExpanded ? styles.expandableWrapperExpanded : '',
  ]
    .filter(Boolean)
    .join(' ');

  const toggleIconClassName = [
    styles.toggleIcon,
    isExpanded ? styles.toggleIconExpanded : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={cardClassName} data-testid={`strategy-card-${strategy.id}`}>
      {isRecommended && (
        <span
          className={styles.recommendedBadge}
          data-testid="recommended-badge"
        >
          RECOMMENDED
        </span>
      )}

      {/* Header - always visible (collapsed state) */}
      <div
        className={styles.headerArea}
        onClick={onToggle}
        role="button"
        aria-expanded={isExpanded}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        data-testid={`strategy-header-${strategy.id}`}
      >
        <div className={styles.headerLeft}>
          <div className={styles.nameRow}>
            <span className={styles.emoji}>{emoji}</span>
            <Typography
              as="h3"
              variant="title"
              size="large"
              className={styles.name}
            >
              {strategy.name}
            </Typography>
          </div>

          {strategy.tagline && (
            <Typography variant="body" size="medium" className={styles.tagline}>
              {strategy.tagline}
            </Typography>
          )}

          {strategy.pills.length > 0 && (
            <div className={styles.pillsContainer}>
              {strategy.pills.map((pill, index) => (
                <span key={index} className={styles.pill}>
                  {pill}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className={styles.selectButtonWrapper}>
          <Button
            variant="filled"
            color="neutral"
            size="medium"
            onClick={handleSelect}
            testId={`strategy-select-${strategy.id}`}
          >
            Select
          </Button>
        </div>
      </div>

      {/* Expandable content - features + case study */}
      {hasExpandableContent && (
        <div className={expandableWrapperClassName}>
          <div className={styles.expandableContent} role="region">
            <div className={styles.expandedInner}>
              {displayFeatures.length > 0 && (
                <ul className={styles.featureList}>
                  {displayFeatures.map((feature, index) => (
                    <li key={index} className={styles.featureItem}>
                      <FontAwesomeIcon
                        icon={faCheck}
                        className={styles.checkIcon}
                      />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              )}

              {caseStudy && (
                <div className={styles.caseStudySection}>
                  <div className={styles.caseStudyCardWrapper}>
                    <StrategyCaseStudyCard
                      caseStudy={caseStudy}
                      testId={`strategy-case-study-${caseStudy.id}`}
                    />
                  </div>
                </div>
              )}

              {/* Show less / Show more toggle */}
              <button
                type="button"
                className={styles.toggleButton}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle();
                }}
                data-testid={`strategy-toggle-${strategy.id}`}
              >
                {isExpanded ? 'Show less' : 'Show more'}
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={toggleIconClassName}
                />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
